import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Calendar, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatCurrency, PROFIL_LABELS } from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const PROG_C = { "174": "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400", mpr: "bg-blue-500/20 text-blue-600 dark:text-blue-400", MPR: "bg-blue-500/20 text-blue-600 dark:text-blue-400", cumule: "bg-violet-500/20 text-violet-600 dark:text-violet-400" };
const PROG_L = { "174": "174", mpr: "MPR", MPR: "MPR", cumule: "Op. Cumulée" };

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors"
      onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        {label}
        {active ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </div>
    </th>
  );
}

export default function EtudesRegie() {
  const navigate = useNavigate();
  const [etudes, setEtudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [programmeTab, setProgrammeTab] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const source = localStorage.getItem("label_energie_user") || "regie";

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  useEffect(() => {
    fetch(`${API}/etudes?source=${encodeURIComponent(source)}&saved_only=true`)
      .then(r => r.json()).then(setEtudes).catch(() => {}).finally(() => setLoading(false));
  }, [source]);

  const sorted = useMemo(() => {
    const filtered = etudes.filter(e => {
      if (search) {
        const s = search.toLowerCase();
        if (!(e.beneficiaire?.nom || "").toLowerCase().includes(s) && !(e.id || "").toLowerCase().includes(s)) return false;
      }
      if (programmeTab !== "all") {
        const rec = (e.recommendation || "").toLowerCase();
        if (programmeTab === "mpr" && rec !== "mpr") return false;
        if (programmeTab === "174" && rec !== "174") return false;
        if (programmeTab === "cumule" && rec !== "cumule" && rec !== "cumulée") return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case "nom": va = (a.beneficiaire?.nom || "").toLowerCase(); vb = (b.beneficiaire?.nom || "").toLowerCase(); break;
        case "profil": va = a.profil || ""; vb = b.profil || ""; break;
        case "surface": va = parseFloat(a.surface) || 0; vb = parseFloat(b.surface) || 0; break;
        case "zone": va = a.zone || ""; vb = b.zone || ""; break;
        case "programme": va = a.recommendation || ""; vb = b.recommendation || ""; break;
        case "commission": va = a.regieOptions?.[0]?.earnings || 0; vb = b.regieOptions?.[0]?.earnings || 0; break;
        case "created_at": va = a.created_at || ""; vb = b.created_at || ""; break;
        default: va = a.id || ""; vb = b.id || "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [etudes, search, programmeTab, sortField, sortDir]);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="etudes-regie-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Mes Études</h1>
        <p className="text-sm text-muted-foreground mt-1">{sorted.length} étude(s) enregistrée(s)</p>
      </div>

      {/* Programme tabs */}
      <div className="flex gap-1 border-b border-border pb-1">
        {[
          { key: "all", label: "Toutes" },
          { key: "mpr", label: "MPR" },
          { key: "174", label: "174" },
          { key: "cumule", label: "Op. Cumulée" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setProgrammeTab(tab.key)}
            className={cn("px-4 py-2 text-sm font-medium rounded-t-lg transition-colors",
              programmeTab === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            )} data-testid={`tab-etude-prog-${tab.key}`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou ID..." className="pl-9 rounded-xl" data-testid="search-etudes" />
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground">{etudes.length === 0 ? "Aucune étude enregistrée" : "Aucun résultat"}</p>
        </div>
      ) : (
        <div className="card-floating overflow-hidden overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-border bg-muted/30">
                <SortHeader label="ID" field="id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Nom bénéficiaire" field="nom" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground">Tél.</th>
                <SortHeader label="Catégorie" field="profil" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Surface" field="surface" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Zone" field="zone" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Programme" field="programme" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Commission" field="commission" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Date" field="created_at" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(e => (
                <tr key={e.id} className="border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors"
                  onClick={() => navigate(`/etude/${e.id}`)} data-testid={`etude-row-${e.id}`}>
                  <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">#{e.id?.slice(-6)}</td>
                  <td className="px-3 py-2.5 text-sm font-semibold">{e.beneficiaire?.nom || "-"}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{e.beneficiaire?.telephone || "-"}</td>
                  <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-muted/50 text-gray-700 font-medium">{PROFIL_LABELS?.[e.profil] || e.profil || "-"}</span></td>
                  <td className="px-3 py-2.5 text-xs">{e.surface || 0} m²</td>
                  <td className="px-3 py-2.5 text-xs">{e.zone || "-"}</td>
                  <td className="px-3 py-2.5"><span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase", PROG_C[e.recommendation] || "bg-muted/50 text-gray-600")}>{PROG_L[e.recommendation] || e.recommendation || "-"}</span></td>
                  <td className="px-3 py-2.5 text-xs font-bold text-orange-600">{e.regieOptions?.[0]?.earnings ? formatCurrency(e.regieOptions[0].earnings) : "-"}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtDate(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
